import { BLACK, Rgb, WHITE, contrastRatio, ensureContrast, luminance, mix, parseColor, toHex } from './color.util';

// Same fallbacks the agent SCSS uses for --fa-bg / --fa-text / --fa-accent.
const DEFAULT_BG = '#121111';
const DEFAULT_TEXT = '#f1eded';
const DEFAULT_ACCENT = '#eba921';

/** Bot bubbles are --fa-text at 8% over --fa-bg (.fa-msg-bot .fa-msg-bubble). */
const BUBBLE_TEXT_MIX = 0.08;

function hueSat([r, g, b]: Rgb): [number, number] {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  if (d === 0) {
    return [0, 0];
  }
  const l = (max + min) / 2;
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = max === rn ? ((gn - bn) / d) % 6 : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4;
  return [(h * 60 + 360) % 360, s];
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return toHex([(r + m) * 255, (g + m) * 255, (b + m) * 255]);
}

/** Hue for derived links: the panel's own (navy → blue, green → green), else the accent's on a grey/black panel. */
function linkHue(bg: Rgb, accent: Rgb): number | null {
  const [bgHue, bgSat] = hueSat(bg);
  if (bgSat >= 0.08) {
    return bgHue;
  }
  const [accentHue, accentSat] = hueSat(accent);
  return accentSat >= 0.08 ? accentHue : null;
}

/**
 * The link colour in `hue` that sits furthest from the body text while still reading 4.5:1 on the
 * bubble. A near-white tint passes on the bubble but looks like plain text next to white copy
 * (e.g. "Sign in or sign up" mid-sentence), so the step walks from the bubble side toward the text.
 */
function linkShade(hue: number, bubble: string, dark: boolean): string {
  const bubbleLum = luminance(parseColor(bubble));
  for (let step = 0; step <= 60; step++) {
    const lightness = dark ? 0.35 + step / 100 : 0.65 - step / 100;
    const color = hslToHex(hue, 1, Math.min(0.95, Math.max(0.05, lightness)));
    if (contrastRatio(luminance(parseColor(color)), bubbleLum) >= 4.6) {
      return color;
    }
  }
  return dark ? '#FFFFFF' : '#000000';
}

/**
 * CSS variables for AI agent text that sits on the bot bubble, derived from the network's or
 * group's ai_agent_* webpage settings. The accent stays the link colour whenever it is readable
 * (4.5:1) on the bubble; an accent that only works as a fill (e.g. red on navy) falls back to a
 * saturated shade of the background hue that also stands apart from the body text.
 * Scrollbars mix background and text so they stay subtle on any theme.
 */
export function agentThemeVars(theme: any): Record<string, string> {
  const bg = parseColor(theme?.ai_agent_bg_color) || parseColor(DEFAULT_BG);
  const text = parseColor(theme?.ai_agent_text_color) || parseColor(DEFAULT_TEXT);
  const accentHex = parseColor(theme?.ai_agent_accent_color) ? theme.ai_agent_accent_color : DEFAULT_ACCENT;
  const accent = parseColor(accentHex);

  const bubble = toHex(mix(bg, text, BUBBLE_TEXT_MIX));
  const bubbleLum = luminance(parseColor(bubble));
  const dark = contrastRatio(1, bubbleLum) >= contrastRatio(0, bubbleLum);
  const accentReadable = contrastRatio(luminance(accent), bubbleLum) >= 4.5;

  const hue = linkHue(bg, accent);
  const derived = hue === null ? ensureContrast(toHex(mix(text, parseColor(bubble), 0.3)), bubble) : linkShade(hue, bubble, dark);
  const link = accentReadable ? toHex(accent) : derived;
  const linkHover = accentReadable
    ? ensureContrast(toHex(mix(accent, dark ? WHITE : BLACK, 0.25)), bubble)
    : toHex(mix(parseColor(link), text, 0.3));
  const linkIcon = link;

  return {
    '--fa-link': link,
    '--fa-link-hover': linkHover,
    '--fa-link-icon': linkIcon,
    '--fa-divider': toHex(mix(parseColor(bubble), text, 0.14)),
    '--fa-scroll-thumb': toHex(mix(bg, text, 0.2)),
    '--fa-scroll-thumb-hover': toHex(mix(bg, text, 0.38)),
    '--fa-scroll-track': toHex(mix(bg, BLACK, dark ? 0.25 : 0.05)),
  };
}
